import { Component, Inject, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { NgForm } from '@angular/forms';
import { MAT_DIALOG_DATA, MatAutocompleteTrigger, MatDialog, MatDialogRef } from '@angular/material';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';
import { CommonConstants } from 'src/app-core/constants/common-constants';
import { CustomPattern } from 'src/app-core/pattern/custom-pattern';
import { CommonService } from 'src/app-core/services/common.service';
import { ContactDataModel } from 'src/app/contacts/contact-data-model';
import { ContactService } from 'src/app/contacts/contact.service';
import { CustomerService } from 'src/app/customer/customer.service';
import { AddAddressComponent } from '../../add-address/add-address.component';
import { CommonDataModel } from '../../common-data-model';

@Component({
    selector: 'app-contact-form-dialog',
    templateUrl: './contact-form-dialog.component.html',
    styleUrls: ['./contact-form-dialog.component.scss'],
    encapsulation: ViewEncapsulation.None,
})

export class ContactFormDialogComponent implements OnInit {

    @ViewChild('contactForm', { static: true }) contactForm: NgForm;
    @ViewChild('matAutocomplete', { read: MatAutocompleteTrigger, static: false }) autoComplete: MatAutocompleteTrigger;
    @ViewChild('matAccountAutocomplete', { read: MatAutocompleteTrigger, static: false }) accountAutocomplete: MatAutocompleteTrigger;

    pattern = new CustomPattern();
    commonDataModel = new CommonDataModel();
    contactDataModel = new ContactDataModel();
    contactFormList: any = this.commonDataModel.contactFormModel;
    contactPhoneListModel = this.commonDataModel.contactPhoneListModel;
    accounts: any = [];
    addresses: any = [];
    contactData: any;
    isFormLoading = false;
    isManuallySelectedAccount = false;
    isOnOrderOverview = false;
    commonConstants: typeof CommonConstants = CommonConstants;

    constructor(
        public dialog: MatDialog,
        public dialogRef: MatDialogRef<ContactFormDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: MatDialog,
        private contactService: ContactService,
        private commonService: CommonService,
        private accountService: CustomerService,
        private toastr: ToastrService,
        private router: Router
    ) {
        this.contactData = this.data;
    }

    ngOnInit() {
        if (this.router.url.indexOf('app/quotes/quote-details') !== -1 || this.router.url.indexOf('app/jobs/job-details') !== -1) {
            this.isOnOrderOverview = true;
        } else {
            this.isOnOrderOverview = false;
        }
        window.addEventListener('scroll', this.autoCompleteScrollEvent, true);
        this.leadItems();
    }

    leadItems(): void {
        if (this.contactData && this.contactData.contactId) {
            this.isFormLoading = true;
            if (this.contactData.isBooking) {
                this.editContactBooking();
            } else {
                this.editContact();
            }
        }
        if (this.contactData && this.contactData.accountId) {
            this.getAccount();
        }
    }

    onSaveContact(): void {
        if (this.contactFormList.primaryPhone) {
            const primaryContact = this.contactFormList.primaryPhone.replace(/\D/g, '');
            if (primaryContact && primaryContact.length < 10 && !this.contactFormList.isInternationalPhone) {
                this.toastr.warning(CommonConstants.appComponent.invalidPhoneNumber);
                return;
            }
        }
        const data = JSON.parse(JSON.stringify(this.contactFormList));
        delete data.account;
        delete data.address;
        data.accountId = this.contactFormList.account.accountId;
        if (this.contactData.isBooking) {
            data.IsOrderUpdate = true;
            data.OrderId = this.contactData.orderId;
        }
        const filterData = this.contactFormList.contactNumberList.filter((list, index, self) => {
            return self.map(mapObj => mapObj.phoneTypeId).indexOf(list.phoneTypeId) === index;
        });

        if (data.accountId) {
            if (filterData.length === this.contactFormList.contactNumberList.length) {
                this.isFormLoading = true;
                if (data.contactId) {
                    this.contactService.updateContact(data)
                        .pipe(finalize(() => (this.isFormLoading = false)))
                        .subscribe(response => {
                            if (response.isSuccess) {
                                if (this.isOnOrderOverview) {
                                    this.onClose({ success: true, response: data });
                                    this.toastr.success(CommonConstants.contactListConstants.contactUpdatedSuccessfully);
                                } else {
                                    response.confirmResult = true;
                                    this.onClose(response);
                                    this.toastr.success(CommonConstants.contactListConstants.contactUpdatedSuccessfully);
                                }
                            }
                        });
                } else {
                    this.contactService.createContact(data)
                        .pipe(finalize(() => (this.isFormLoading = false)))
                        .subscribe(response => {
                            if (response.isSuccess) {
                                response.data.confirmResult = true;
                                this.onClose(response);
                                this.toastr.success(CommonConstants.contactListConstants.contactAddedSuccessfully);
                            }
                        });
                }
            } else {
                this.isFormLoading = false;
                this.toastr.warning(CommonConstants.contactListConstants.duplicationOfPhoneTypes);
            }
        } else {
            this.toastr.warning(CommonConstants.appComponent.selectAccount);
        }
    }

    editContact(): void {
        this.contactService.editContact(this.contactData.contactId).subscribe(response => {
            if (response.isSuccess) {
                this.contactFormList = response.data;
                if (this.contactData.isEdit) {
                    this.contactData.accountId = response.data.accountId;
                    this.contactData.accountTypeId = response.data.accountTypeId;
                }
                this.getAccountDetails();
            }
        });
    }

    editContactBooking(): void {
        this.contactService.editContactBooking(this.contactData.contactId).subscribe(response => {
            if (response.isSuccess) {
                this.contactFormList = response.data;
                if (this.contactData.isEdit) {
                    this.contactData.accountId = response.data.accountId;
                    this.contactData.accountTypeId = response.data.accountTypeId;
                }
                this.getAccount();
            }
        });
    }

    getAccount(): void {
        this.commonService.getAccounts(this.contactData.accountName).subscribe(response => {
            if (response.isSuccess) {
                const accountList = response.data;
                if (!this.contactData.isEdit) {
                    this.accounts = response.data;
                }
                const account = accountList.find(x => x.accountId === this.contactData.accountId);
                this.contactFormList.account = account;
                this.contactFormList.accountId = account.accountId;
                this.getAddresses();
            }
        });
    }

    onChangeAccount(searchText: string): void {
        this.accounts = [];
        if (searchText && searchText.length >= 3) {
            this.commonService.getAccounts(searchText).subscribe(response => {
                if (response.isSuccess) {
                    this.accounts = this.contactFormList.account.length >= 3 ? response.data : [];
                }
            });
        }
    }

    onSetAccountDetails(account: any): void {
        if (account && account.accountId) {
            this.contactFormList.accountId = account.accountId;
            this.contactFormList.accountTypeId = account.accountTypeId;
            this.addresses = [];
            this.contactFormList.account = {
                accountId: account.accountId,
                accountName: account.accountName,
                accountTypeId: account.accountTypeId
            };
            this.contactData.accountId = account.accountId;
            this.getAddresses();
            this.isManuallySelectedAccount = true;
        }
    }

    onBindAccountName(item: any): string | undefined {
        return item ? item.accountName : undefined;
    }

    getAddresses(searchText?: string): void {
        if (this.contactData.accountId) {
            let searchQuery = `AccountId=${this.contactData.accountId}`;
            if (searchText) {
                searchQuery += `&searchQuery=${searchText}`;
            }
            this.contactService.getAddresses(searchQuery).subscribe((response: any) => {
                if (response.isSuccess) {
                    this.addresses = response.data;
                    if (this.addresses.length > 0 && this.contactData.isEdit === true) {
                        const linkedAddressId = this.contactFormList.linkedAddressId;
                        if (linkedAddressId) {
                            const address = this.addresses.find(x => x.addressId === linkedAddressId);
                            this.contactFormList.address = address;
                        }
                    }
                    this.isFormLoading = false;
                }
            });
        }
    }

    onChangeAddress(searchText: string): void {
        if (searchText && searchText.length >= 3 && this.contactFormList.account.accountId) {
            this.addresses = [];
            this.getAddresses(searchText);
        }
    }

    onSetAddressId(address: any): void {
        this.contactFormList.linkedAddressId = null;
        if (address && address.addressId) {
            this.contactFormList.linkedAddressId = address.addressId;
        }
    }

    onOpenDialog(): void {
        const dialogRef = this.dialog.open(AddAddressComponent, {
            autoFocus: false,
            position: {
                right: '0',
            },
            panelClass: 'small_layout',
            hasBackdrop: false,
            data: {
                accountId: this.contactFormList.accountId,
                accountTypeId: this.contactFormList.accountTypeId
            },
        }).afterClosed()
            .subscribe(resposne => {
                this.getAddresses();
            });
    }

    onBindAddressName(item: any): string | undefined {
        return item ? item.address : undefined;
    }

    onAddPhone(): void {
        if (this.contactFormList.contactNumberList.length < 3) {
            this.contactFormList.contactNumberList.push(Object.assign({}, this.contactPhoneListModel));
        }
    }

    onLinkAddressClose(): void {
        this.isFormLoading = true;
        this.contactFormList.address = '';
        this.contactFormList.linkedAddressId = null;
        this.addresses = [];
        const searchQuery = `AccountId=${this.contactData.accountId}&AccountTypeId=${this.contactFormList.accountTypeId}`;
        this.contactService.getAddresses(searchQuery).subscribe((response: any) => {
            if (response.isSuccess) {
                this.addresses = response.data;
                this.autoComplete.openPanel();
                this.isFormLoading = false;
            }
        });
    }

    onRemovePhone(i: number): void {
        this.contactFormList.contactNumberList.splice(i, 1);
        this.contactForm.form.markAsDirty();
    }

    onClose(contactId?: any): void {
        this.dialogRef.close(contactId);
    }

    autoCompleteScrollEvent = (event: any): void => {
        if (event.target && event.target.className && event.target.className.indexOf('mat-autocomplete-visible') === -1) {
            if (this.accountAutocomplete && this.accountAutocomplete.panelOpen) {
                this.accountAutocomplete.closePanel();
            } else if (this.autoComplete && this.autoComplete.panelOpen) {
                this.autoComplete.closePanel();
            }
        }
    }

    onOpenAccountOption() {
        this.accountAutocomplete.openPanel();
    }

    onOpenLinkAddressOption() {
        this.autoComplete.openPanel();
    }

    emitOnRemovePhone(event: any) {
        if (event) {
            this.contactForm.form.markAsDirty();
        }
    }

    getAccountDetails() {
        this.accountService.getAccount(this.contactData.accountId).subscribe(response => {
            if (response.isSuccess) {
                this.contactFormList.account = {
                    accountId: response.data.id,
                    accountName: response.data.accountName,
                    accountTypeId: response.data.accountTypeId,
                    displayId: response.data.accountDisplayId,
                    suffix: response.data.isCustomer ? '(CUS)' : '(LED)'
                };
                this.getAddresses();
            }
        });
    }
}
